import { action, computed, makeObservable, observable } from "mobx";

/**
 * Manages observable UI state for WeeklyRotaDetailsViewModel.
 * Note: addStudentViewModel is kept on the ViewModel itself (not here) to avoid
 * a circular import chain through AddStudentViewModel → WeeklyRotaDetailsViewModel.
 */
export class MobXManager {
    public _weeklyRotaModalOpen: boolean = false;
    public _mentorAbsenceModalOpen: boolean = false;
    public _sessionLeftModalOpen: boolean = false;
    public _rotaEditable: boolean = false;
    public _mentorStudentAdded: boolean = false;
    public _successfullyGetWeeklyRota: boolean = false;
    public _sessionKey: string = "";
    // Modal state previously held as local React useState in ScheduleChart
    public _openAddStudentModal: boolean = false;
    public _openOverTimeConfirmationModal: boolean = false;
    public _openStudentDetailModal: boolean = false;
    public _resetRotaConfirmationOpen: boolean = false;
    // Session currently being removed from the rota; drives the per-slot delete spinner.
    public _deletingStudentSessionId: Guid | null = null;

    constructor() {
        makeObservable<
            MobXManager,
            | "_weeklyRotaModalOpen"
            | "_mentorAbsenceModalOpen"
            | "_sessionLeftModalOpen"
            | "_rotaEditable"
            | "_mentorStudentAdded"
            | "_successfullyGetWeeklyRota"
            | "_sessionKey"
            | "_openAddStudentModal"
            | "_openOverTimeConfirmationModal"
            | "_openStudentDetailModal"
            | "_resetRotaConfirmationOpen"
            | "_deletingStudentSessionId"
        >(this, {
            _weeklyRotaModalOpen: observable,
            _mentorAbsenceModalOpen: observable,
            _sessionLeftModalOpen: observable,
            _rotaEditable: observable,
            _mentorStudentAdded: observable,
            _successfullyGetWeeklyRota: observable,
            _sessionKey: observable,
            _openAddStudentModal: observable,
            _openOverTimeConfirmationModal: observable,
            _openStudentDetailModal: observable,
            _resetRotaConfirmationOpen: observable,
            _deletingStudentSessionId: observable,
            showAddStudentModal: computed,
            setWeeklyRotaModalOpen: action,
            setMentorAbsenceModalOpen: action,
            setSessionLeftModalOpen: action,
            setRotaEditable: action,
            setMentorStudentAdded: action,
            setSuccessfullyGetWeeklyRota: action,
            setSessionKey: action,
            setOpenAddStudentModal: action,
            setOpenOverTimeConfirmationModal: action,
            setOpenStudentDetailModal: action,
            setResetRotaConfirmationOpen: action,
            setDeletingStudentSessionId: action,
        });
    }

    public get weeklyRotaModalOpen(): boolean {
        return this._weeklyRotaModalOpen;
    }
    public get mentorAbsenceModalOpen(): boolean {
        return this._mentorAbsenceModalOpen;
    }
    public get sessionLeftModalOpen(): boolean {
        return this._sessionLeftModalOpen;
    }
    public get rotaEditable(): boolean {
        return this._rotaEditable;
    }
    public get mentorStudentAdded(): boolean {
        return this._mentorStudentAdded;
    }
    public get successfullyGetWeeklyRota(): boolean {
        return this._successfullyGetWeeklyRota;
    }
    public get sessionKey(): string {
        return this._sessionKey;
    }
    public get openAddStudentModal(): boolean {
        return this._openAddStudentModal;
    }
    /** True when the Add Student modal should be visible (slot selected and student not yet added). */
    public get showAddStudentModal(): boolean {
        return this._openAddStudentModal && !this._mentorStudentAdded;
    }
    public get openOverTimeConfirmationModal(): boolean {
        return this._openOverTimeConfirmationModal;
    }
    public get openStudentDetailModal(): boolean {
        return this._openStudentDetailModal;
    }

    public setWeeklyRotaModalOpen(value: boolean): void {
        this._weeklyRotaModalOpen = value;
    }
    public setMentorAbsenceModalOpen(value: boolean): void {
        this._mentorAbsenceModalOpen = value;
    }
    public setSessionLeftModalOpen(value: boolean): void {
        this._sessionLeftModalOpen = value;
    }
    public setRotaEditable(value: boolean): void {
        this._rotaEditable = value;
    }
    public setMentorStudentAdded(value: boolean): void {
        this._mentorStudentAdded = value;
    }
    public setSuccessfullyGetWeeklyRota(value: boolean): void {
        this._successfullyGetWeeklyRota = value;
    }
    public setSessionKey(value: string): void {
        this._sessionKey = value;
    }
    public setOpenAddStudentModal(value: boolean): void {
        this._openAddStudentModal = value;
    }
    public setOpenOverTimeConfirmationModal(value: boolean): void {
        this._openOverTimeConfirmationModal = value;
    }
    public setOpenStudentDetailModal(value: boolean): void {
        this._openStudentDetailModal = value;
    }
    public get resetRotaConfirmationOpen(): boolean {
        return this._resetRotaConfirmationOpen;
    }
    public setResetRotaConfirmationOpen(value: boolean): void {
        this._resetRotaConfirmationOpen = value;
    }
    public get deletingStudentSessionId(): Guid | null {
        return this._deletingStudentSessionId;
    }
    public setDeletingStudentSessionId(value: Guid | null): void {
        this._deletingStudentSessionId = value;
    }
}
